import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { tenantsApi, organizationsApi, Tenant, Organization, CreateTenantDto, UpdateTenantDto } from '../utils/api';
import { AdminLayout } from '../components/AdminLayout';
import { formatDate } from '../utils/formatting';
import { resolveOrganizationName, getAvailableOrganizations } from '../utils/organizations';

export const Tenants: React.FC = () => {
  const { token, isGlobalAdmin } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [formData, setFormData] = useState<CreateTenantDto>({
    organization_id: '',
    is_active: true,
    invoice_prefix: '',
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    if (!token) return;

    try {
      setLoading(true);
      const [tenantsData, orgsData] = await Promise.all([
        tenantsApi.getAll(token),
        organizationsApi.getAll(token),
      ]);
      setTenants(tenantsData);
      setOrganizations(orgsData);
      setError('');
    } catch (err) {
      setError(t('tenants.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    if (!formData.organization_id) {
      setFormError(t('tenants.organizationRequired'));
      return;
    }

    setFormLoading(true);
    setFormError('');

    try {
      await tenantsApi.create(token, formData);
      setShowCreateModal(false);
      setFormData({ organization_id: '', is_active: true, invoice_prefix: '' });
      await fetchData();
    } catch (err: any) {
      setFormError(err.message || t('tenants.failedToCreate'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !selectedTenant) return;

    setFormLoading(true);
    setFormError('');

    try {
      const updateData: UpdateTenantDto = {
        organization_id: formData.organization_id,
        is_active: formData.is_active,
        invoice_prefix: formData.invoice_prefix,
      };
      await tenantsApi.update(token, selectedTenant.tenant_id, updateData);
      setShowEditModal(false);
      setSelectedTenant(null);
      setFormData({ organization_id: '', is_active: true, invoice_prefix: '' });
      await fetchData();
    } catch (err: any) {
      setFormError(err.message || t('tenants.failedToUpdate'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteTenant = async () => {
    if (!token || !selectedTenant) return;

    setFormLoading(true);
    setFormError('');

    try {
      await tenantsApi.delete(token, selectedTenant.tenant_id);
      setShowDeleteModal(false);
      setSelectedTenant(null);
      await fetchData();
    } catch (err: any) {
      setFormError(err.message || t('tenants.failedToDelete'));
    } finally {
      setFormLoading(false);
    }
  };

  const openEditModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      organization_id: tenant.organization_id,
      is_active: tenant.is_active,
      invoice_prefix: tenant.invoice_prefix,
    });
    setFormError('');
    setShowEditModal(true);
  };

  const openDeleteModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setFormError('');
    setShowDeleteModal(true);
  };

  const openCreateModal = () => {
    setFormData({ organization_id: '', is_active: true, invoice_prefix: '' });
    setFormError('');
    setShowCreateModal(true);
  };

  return (
    <AdminLayout title={t('tenants.title')} subtitle={t('tenants.subtitle')}>
      <div className="">
        {/* Header with Create Button - Only visible to global admins */}
        {isGlobalAdmin && (
          <div className="mb-6 flex justify-end">
            <button
              onClick={openCreateModal}
              className="flex items-center gap-2 bg-[var(--navy)] text-white px-4 py-2 rounded-lg font-medium hover:bg-[var(--navy-light)] transition-colors"
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              {t('tenants.createTenant')}
            </button>
          </div>
        )}

        {/* Loading/Error States */}
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block w-10 h-10 border-3 border-gray-200 border-t-[var(--gold)] rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500">{t('tenants.loadingTenants')}</p>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-red-600">{error}</p>
          </div>
        ) : (
          <div className="rounded-lg overflow-hidden" style={{ background: 'var(--surface)', boxShadow: 'var(--shadow)' }}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead style={{ borderBottom: '1px solid var(--border)', background: 'var(--gold-pale)' }}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {t('tenants.organization')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {t('tenants.invoicePrefix')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {t('common.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {t('common.created')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tenants.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      {t('tenants.noTenants')}
                    </td>
                  </tr>
                ) : (
                  tenants.map((tenant) => (
                    <tr key={tenant.tenant_id} className="hover:bg-[var(--gold-pale)]">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className="w-8 h-8 rounded-full mr-3 flex items-center justify-center"
                            style={{ background: 'var(--gold-pale)' }}
                          >
                            <span className="font-semibold text-sm" style={{ color: 'var(--navy)' }}>
                              {resolveOrganizationName(organizations, tenant.organization_id).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            {resolveOrganizationName(organizations, tenant.organization_id)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{tenant.invoice_prefix}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            tenant.is_active
                              ? 'bg-[var(--success-bg)] text-[var(--success)]'
                              : 'bg-[var(--error-bg)] text-[var(--error)]'
                          }`}
                        >
                          {tenant.is_active ? t('common.active') : t('common.inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(tenant.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => navigate(`/admin/tenants/${tenant.tenant_id}`)}
                          className="text-[var(--navy)] hover:text-[var(--navy-light)] mr-4"
                        >
                          {t('common.view')}
                        </button>
                        {isGlobalAdmin && (
                          <>
                            <button
                              onClick={() => openEditModal(tenant)}
                              className="text-[var(--navy)] hover:text-[var(--navy-light)] mr-4"
                            >
                              {t('common.edit')}
                            </button>
                            <button
                              onClick={() => openDeleteModal(tenant)}
                              className="text-red-600 hover:text-red-900"
                            >
                              {t('common.delete')}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-md w-full p-6 my-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">{t('tenants.createNewTenant')}</h3>
            <form onSubmit={handleCreateTenant}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('tenants.organization')} *
                  </label>
                  <select
                    required
                    value={formData.organization_id}
                    onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
                  >
                    <option value="">{t('tenants.selectOrganization')}</option>
                    {getAvailableOrganizations(organizations, tenants).map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name} ({org.code})
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">{t('tenants.organizationHelp')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('tenants.invoicePrefix')}
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={20}
                    value={formData.invoice_prefix || ''}
                    onChange={(e) => setFormData({ ...formData, invoice_prefix: e.target.value })}
                    placeholder="INV"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">{t('tenants.invoicePrefixHelp')}</p>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active_create"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 border-[var(--border)] rounded focus:ring-[var(--gold)]"
                  />
                  <label htmlFor="is_active_create" className="ml-2 text-sm text-gray-700">
                    {t('common.active')}
                  </label>
                </div>
              </div>
              {formError && (
                <div className="mt-4 text-red-600 text-sm">{formError}</div>
              )}
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-[var(--gold-pale)] transition-colors"
                  disabled={formLoading}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[var(--navy)] text-white rounded-lg font-medium hover:bg-[var(--navy-light)] transition-colors disabled:opacity-50"
                  disabled={formLoading}
                >
                  {formLoading ? t('common.creating') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-md w-full p-6 my-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">{t('tenants.editTenant')}</h3>
            <form onSubmit={handleEditTenant}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('tenants.organization')}
                  </label>
                  <input
                    type="text"
                    disabled
                    value={resolveOrganizationName(organizations, selectedTenant.organization_id)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('tenants.invoicePrefix')}
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={20}
                    value={formData.invoice_prefix || ''}
                    onChange={(e) => setFormData({ ...formData, invoice_prefix: e.target.value })}
                    placeholder="INV"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
                  />
                  <div className="mt-2 flex gap-2 rounded-lg bg-amber-50 border border-amber-200 p-2">
                    <svg className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                    <p className="text-xs text-amber-700">{t('tenants.invoicePrefixEditWarning')}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active_edit"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 border-[var(--border)] rounded focus:ring-[var(--gold)]"
                  />
                  <label htmlFor="is_active_edit" className="ml-2 text-sm text-gray-700">
                    {t('common.active')}
                  </label>
                </div>
              </div>
              {formError && (
                <div className="mt-4 text-red-600 text-sm">{formError}</div>
              )}
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-[var(--gold-pale)] transition-colors"
                  disabled={formLoading}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[var(--navy)] text-white rounded-lg font-medium hover:bg-[var(--navy-light)] transition-colors disabled:opacity-50"
                  disabled={formLoading}
                >
                  {formLoading ? t('common.saving') : t('common.saveChanges')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">{t('tenants.deleteTenant')}</h3>
            <p className="text-gray-600 mb-6" dangerouslySetInnerHTML={{ __html: t('tenants.deleteConfirmation', { name: resolveOrganizationName(organizations, selectedTenant.organization_id) }) }} />
            {formError && (
              <div className="mb-4 text-red-600 text-sm">{formError}</div>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-[var(--gold-pale)] transition-colors"
                disabled={formLoading}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteTenant}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={formLoading}
              >
                {formLoading ? t('common.deleting') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};
